import React, { Suspense, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { Polygon } from 'geojson';
import { getValidatedPinLocation } from 'utils/validated-pin-location';
import { getPolygonGeometryFromBBox } from 'utils/geojson-utils';
import styles from './DestintionAreaEditor.module.css';
import Loading from 'components/Loading';

const CMSMap = React.lazy(() => import('components/CMSMap/CMSMap'));

const DEFAULT_AREA_OFFSET = 0.3;

type DestinationAreaEditorProps = Readonly<{
    value: Polygon | null;
    onChange: (value: Polygon) => void;
    pinLocation: { lat: number; lon: number } | null;
    onPinLocationChange: (value: { lat: number; lon: number }) => void;
}>;

export function DestinationAreaEditor({
    value,
    onChange,
    pinLocation = null,
    onPinLocationChange,
}: DestinationAreaEditorProps) {
    const [editedArea, setEditedArea] = useState<Polygon | null>(null);
    const [editedPinLocation, setEditedPinLocation] = useState<{
        lat: number;
        lon: number;
    } | null>(null);
    const [isEditingArea, setIsEditingArea] = useState(false);
    const [isEditingPinLocation, setIsEditingPinLocation] = useState(false);

    const startEditingArea = () => {
        if (value) {
            setEditedArea(value);
        } else if (pinLocation) {
            const area = getPolygonGeometryFromBBox([
                pinLocation.lon - DEFAULT_AREA_OFFSET,
                pinLocation.lat - DEFAULT_AREA_OFFSET,
                pinLocation.lon + DEFAULT_AREA_OFFSET,
                pinLocation.lat + DEFAULT_AREA_OFFSET,
            ]);
            onChange(area);
            setEditedArea(area);
        }
        setIsEditingPinLocation(false);
        setIsEditingArea(true);
    };

    const canAddArea = !!value || !!pinLocation;

    const startEditingPinLocation = () => {
        setEditedPinLocation(pinLocation);
        setIsEditingArea(false);
        setIsEditingPinLocation(true);
    };

    const saveArea = () => {
        setIsEditingArea(false);
        if (editedArea) {
            onChange(editedArea);
        }
    };

    const savePinLocation = () => {
        setIsEditingPinLocation(false);
        if (editedPinLocation && onPinLocationChange) {
            onPinLocationChange(editedPinLocation);
        }
    };

    return (
        <>
            <div className={styles.map__editButton}>
                {isEditingArea ? (
                    <Tooltip title='You need to save the area before you can save the destination' open>
                        <Button onClick={saveArea} type='primary'>
                            Save new area
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title={canAddArea ? undefined : 'Add a location first'}>
                        <Button onClick={startEditingArea} disabled={!canAddArea}>
                            {value ? 'Edit area' : 'Add area'}
                        </Button>
                    </Tooltip>
                )}
                {isEditingPinLocation ? (
                    <Tooltip title='Click on the map to place the new destination pin before saving' open>
                        <Button onClick={savePinLocation} type='primary'>
                            Save new location
                        </Button>
                    </Tooltip>
                ) : (
                    <Button onClick={startEditingPinLocation}>{pinLocation ? 'Edit location' : 'Add location'}</Button>
                )}
            </div>
            <div className={styles.map__inner}>
                <Suspense fallback={<Loading />}>
                    <CMSMap
                        isEditingArea={isEditingArea ? 'interest' : null}
                        isEditingPinLocation={isEditingPinLocation}
                        highlightedAreaOfInterest={value ?? undefined}
                        highlightedSearchArea={value ?? undefined}
                        onAreaChanged={setEditedArea}
                        onPinLocationChanged={setEditedPinLocation}
                        pinLocation={getValidatedPinLocation(isEditingPinLocation ? editedPinLocation : pinLocation)}
                    />
                </Suspense>
            </div>
        </>
    );
}
